/* eslint-disable @typescript-eslint/no-unused-vars */
import { history } from 'umi';
import { cloneDeep, isEmpty } from 'lodash-es';
import { renameOfOptions } from '@gp/utils/common';
import {
  MENU_ID_MAP,
  SLASH_G_POP,
  PROJECT_GROUP_NAMES,
  STATIC_GR_ACTION_STATUS_NAMES,
  PRIORITY_OPTIONS,
  PROGRESS_REFLECT,
} from '@gp/constants/const';
import { STAGE_OPT } from '@gp/pages/Workspace/Product/components/GameOverview/utils/constant';
import type { IProjectSimpleDTO, IStudioBaseDTO, ISearchResult } from '@gp/types/global';
import type {
  CascadeProjectItemType,
  CascadeProjectOptionsType,
  LevelInfoType,
} from '@gp/types/project';
import { CASCADE_INFO_LIST } from '@gp/constants/select';

export interface IProjectDetailsPathUtilsParams {
  id?: string;
  name?: string;
  tab?: string;
}

export const getProjectDetailsPagePath = (params?: IProjectDetailsPathUtilsParams): string => {
  const { id, name, tab } = params || {};
  if (!id) return '';
  const filter = encodeURIComponent(
    JSON.stringify({
      projectId: id,
      project: name || '',
      menuFrom: MENU_ID_MAP['pipeline_all-projects'],
      tab: tab || 'gameDetail',
    }),
  );
  return `${SLASH_G_POP}/Pipeline/ProjectList/Detail?filter=${filter}`;
};

export const getProjectDetailsPagePathToIntelligence = (params?: any): string => {
  if (!params?.id) return '';
  let url = '';
  switch (params?.platform?.toLowerCase()) {
    case 'mobile':
      url = `/intelligence/topCharts/mobileGames/gamesDetails/${params?.id}/overview`;
      break;
    case 'pc':
      url = `/intelligence/topCharts/pcGames/gamesDetails/${params?.id}/overview`;
      break;
    case 'console':
      url = `/intelligence/topCharts/consoleGames/gamesDetails/${params?.id}/overview`;
      break;
    case 'combine':
      url = `/intelligence/topCharts/pcConsoleGames/gamesDetails/${params?.id}/overview`;
      break;
    default:
      break;
  }
  return url;
};

export const goToProjectDetailsPageByPath = (path?: string) => {
  if (!path) return;
  history.push(path);
};

export const goToProjectDetailsPage = (params: IProjectDetailsPathUtilsParams) => {
  const path = getProjectDetailsPagePath(params);
  goToProjectDetailsPageByPath(path);
};

export const toProjectDetails = (
  params: IProjectDetailsPathUtilsParams,
  e: React.SyntheticEvent | null,
  isJump = true,
) => {
  const url = getProjectDetailsPagePath(params);
  // 点击按钮的时候 跳转页面
  if (isJump && url) {
    e?.stopPropagation();
    e?.preventDefault();
    history.push(url);
  }
  return url;
};

// 根据game_id判断是跳转GPOP还是情报还是不跳转
export const toProjectDetailsByGameType = (
  params: IProjectDetailsPathUtilsParams,
  e: any = null,
  isJump: any = false,
  projects: any = [],
) => {
  let url = '';
  const idValue = params?.id || '';
  const nameValue = params?.name || '';

  const nameList = projects?.map((item: any) => item.project) || [];
  // const idList = projects?.map((item: any) => item.project_id) || [];

  // 判断是否为GPOP已有项目
  if (Array.isArray(nameList) && nameList?.length > 0 && nameList?.includes(nameValue)) {
    const objTemp = projects?.find((item: any) => item.project === nameValue) || {};
    const paramsTemp = {
      ...params,
      id: objTemp?.project_id,
    };
    url = getProjectDetailsPagePath(paramsTemp);
  } else if (idValue && !/^\d+$/.test(idValue)) {
    // 情报ID,跳转情报
    url = getProjectDetailsPagePathToIntelligence(params);
  }

  // 点击按钮的时候 跳转页面
  if (isJump && url) {
    e?.stopPropagation();
    e?.preventDefault();
    history.push(url);
  }

  return url;
};

// 根据id判断是否能跳转
export const ifCanGoToDetailsByGameType = (cvm: any, projects: any) => {
  const { game_id: projectId, game_name: project, platform } = cvm || {};
  const params = {
    id: projectId as any,
    name: project,
    tab: 'gameDetail',
    platform,
  };
  const idValue = params?.id || '';
  const nameValue = params?.name || '';
  const nameList = projects?.map((item: any) => item.project) || [];

  // 判断是否为GPOP已有项目
  if (Array.isArray(nameList) && nameList?.length > 0 && nameList?.includes(nameValue)) {
    return true;
  }
  if (idValue && !/^\d+$/.test(idValue)) {
    return true;
  }
  return false;
};

export const getProjectGroupName = (name?: string): string => {
  if (!name) return '';
  return PROJECT_GROUP_NAMES[name] || name || '';
};

export const renameOfProjectGroupOptions = (
  options?: IGPSelectOptionWithGroup[] | null,
): IGPSelectOptionWithGroup[] => {
  return renameOfOptions(options, getProjectGroupName);
};

export const formStatusOptions = (options?: any) => {
  if (Array.isArray(options)) {
    return options?.map((item: any) => {
      const valueTmp = String(PROGRESS_REFLECT[item?.value]);
      return { ...item, value: valueTmp };
    });
  }
  return options;
};

/**
 * 处理Business Owner，由于历史数据中存在JSON字符串格式的Business Owner，所以需要这个特殊处理
 * @param businessOwner businessOwner字符串
 * @param placeholder 占位符
 * @return 处理后的businessOwner字符串
 */
export const getProjectBusinessOwner = (businessOwner?: string, placeholder = ''): string => {
  let businessOwnerTmp = businessOwner || '';
  try {
    // @ts-ignore
    businessOwnerTmp = Object.values(JSON.parse(businessOwnerTmp))[0] || businessOwnerTmp;
  } catch (error) {}
  return businessOwnerTmp || placeholder;
};

export const getProjectPriorityName = (value?: string): string => {
  if (!value) return '';
  const priority = PRIORITY_OPTIONS.find(
    (item) => item?.value?.toLowerCase() === value?.toLowerCase(),
  );
  return priority?.text || value || '';
};

export const getProjectStageName = (value?: string): string => {
  if (!value) return '';
  const stage = STAGE_OPT.find((item) => item?.value?.toLowerCase() === value?.toLowerCase());
  return stage?.text || value || '';
};

export const getActionItemStatusNameByText = (name?: string) => {
  if (!name) return '';
  return STATIC_GR_ACTION_STATUS_NAMES[name?.toLowerCase()] || name;
};

export const sortStages = (list?: string[] | null): string[] => {
  if (!Array.isArray(list) || !list.length) return [];
  const sortedList = cloneDeep(list);

  sortedList.sort((a, b) => {
    let aIndex = -1;
    let bIndex = -1;

    for (let i = 0; i < STAGE_OPT.length; i++) {
      const { value } = STAGE_OPT[i] || {};
      const valueLowerCase = value?.toLowerCase() || '';
      if (aIndex === -1 && valueLowerCase === a?.toLowerCase()) {
        aIndex = i;
      }
      if (bIndex === -1 && valueLowerCase === b?.toLowerCase()) {
        bIndex = i;
      }
      if (aIndex !== -1 && bIndex !== -1) break;
    }

    return aIndex - bIndex;
  });

  return sortedList;
};

// 按项目名称的首字母顺序（1-10，A-Z）排序
export const sortByProName = (proList: any[]) => {
  if (!Array.isArray(proList) || !proList.length) return proList;
  const proListTemp = [...proList];
  proListTemp?.sort((a, b) => {
    const nameA = a.name || a.projectName || a.project || '';
    const nameB = b.name || b.projectName || b.project || '';
    // 提取首字符并统一转为大写
    const firstCharA = nameA.charAt(0).toUpperCase();
    const firstCharB = nameB.charAt(0).toUpperCase();

    // 判断是否为数字
    const isNumA = !isNaN(firstCharA) && firstCharA !== ' ';
    const isNumB = !isNaN(firstCharB) && firstCharB !== ' ';

    // 数字优先排序
    if (isNumA && !isNumB) return -1;
    if (!isNumA && isNumB) return 1;

    // 同为数字或字母时按字母顺序比较
    return nameA.localeCompare(nameB);
  });
  return proListTemp;
};

/**
 * 把关键字搜索接口返回的数据 转换为 搜索下拉组件展示的列表的数据源
 * @param searchResults 关键字搜索接口返回的数据
 * @returns 搜索下拉框组件展示的列表的数据源
 */
export const formatSearchProjectsResults = <T extends IProjectSimpleDTO & IStudioBaseDTO>(
  searchResults?: T[] | null,
): ISearchResult[] => {
  if (!Array.isArray(searchResults) || !searchResults.length) return [];
  return searchResults?.map((searchResult) => {
    const {
      project_id: projectId,
      project: projectName,
      cover: projectCover,
      studio_id: studioId,
      studio_entity_id: studioIdEntity,
      platform,
    } = searchResult || {};

    return {
      id: '',
      project_id: projectId || '',
      name: projectName || '',
      img: projectCover || '',
      studio_id: studioId || studioIdEntity || '',
      platform: (Array.isArray(platform) ? platform?.join(',') : platform) || '',
    };
  });
};

// 扁平化树结构
export const getCascadeFlatData = (options: any[]) => {
  if (Array.isArray(options) && options?.length > 0) {
    const flatten: CascadeProjectOptionsType[] = [];
    const walk = (nodes: CascadeProjectOptionsType[]) => {
      nodes?.forEach((node) => {
        flatten?.push(node);
        if (node.child) walk(node.child);
      });
    };
    walk(options);
    return flatten;
  }
  return [];
};

// 根据级别判断是否高亮
export const getDefaultActiveList = (isEndPanel: any, index: any, parent: any) => {
  if (isEmpty(parent)) {
    return index === 0;
  }
  if (parent?.active && !isEndPanel) {
    return index === 0;
  }
  return false;
};

// 根据接口返回的原始数据，赋值父子节点信息
export const getCascadeProjectDatas = (
  projectList: CascadeProjectItemType[],
  permissionKey = '',
  cascadeInfoList: any = CASCADE_INFO_LIST,
  parent = {} as any,
  levelValue = 1,
) => {
  return projectList?.map((item: CascadeProjectItemType, index: number) => {
    const infoObj =
      cascadeInfoList?.find((infoItem: LevelInfoType) => infoItem.level === levelValue) ||
      ({} as any);
    const key = infoObj?.key;
    const nameKey = infoObj?.nameKey;
    let stateValue = 'none';
    // 业务代码，根据不同页面与权限，设置默认勾选项
    if (infoObj?.isEndPanel === true && item?.[permissionKey] === true) {
      stateValue = 'all';
    }
    const curKey = item?.[key] || 'Others';
    let curId = curKey;
    if (!infoObj?.isEndPanel) {
      const parKey = parent?.id ? `${parent?.id}_` : '';
      curId = `${parKey + curKey}_${index}`;
    }

    const resObj = {
      ...infoObj,
      id: curId,
      name: item?.[nameKey] || 'Others',
      state: stateValue,
      active: getDefaultActiveList(infoObj?.isEndPanel, index, parent),
    };
    return {
      ...resObj,
      child: getCascadeProjectDatas(
        item.child || [],
        permissionKey,
        cascadeInfoList,
        resObj,
        levelValue + 1,
      ),
      parent,
      parentId: parent.id,
      parentLevel: parent.level,
    };
  });
};

// 获取级联筛选器默认值
export const getCascadeProjectValues = (optionsList?: any[], isDefaultValues = false) => {
  const dataList = getCascadeFlatData(optionsList);
  let filterValue = [...dataList];
  filterValue =
    dataList?.filter(
      (item: any) => (isDefaultValues ? item.state === 'all' : true) && item.isEndPanel,
    ) || [];
  return filterValue?.map((item: any) => item.id) || [];
};

// 递归遍历树型级联，查找对应数据
export const findNameByTreeProjectId = (tree: any, id: string | number) => {
  if (tree && Array.isArray(tree)) {
    for (const node of tree) {
      if (node.id === id) return node.name;
      if (node.child) {
        const result: any = findNameByTreeProjectId(node.child, id);
        if (result) return result;
      }
    }
  }
  return '';
};
